
import { Component, Mixins, Prop, Watch } from 'vue-property-decorator';
import { getMaxScale } from '@/functions';
import { ContentsId } from '@/shared/model';
import { useTelemeterBasinStore } from '@/shared/service';
import BarChart from './BarChart';
import ChartMixin from './ChartMixin.vue';

@Component({
  components: {
    BarChart,
  },
})
export default class RChart extends Mixins(ChartMixin) {
  @Prop({ required: false, default: '' }) propTargetTime?: string;

  private chartOptions = null;

  get colors() {
    return this['$store'].getters.colors;
  }

  get loading() {
    return this['$store'].getters.loading;
  }

  get tabId() {
    return this['$store'].getters.tabId;
  }

  get gridPitch() {
    return this['$store'].getters.gridPitch;
  }

  get isMobile() {
    return this['$store'].getters.isMobile;
  }

  get rerender() {
    return this['$store'].getters.rerender;
  }

  set rerender(value) {
    this['$store'].commit('setRerender', value);
  }

  get kouuScales() {
    return this['$store'].getters.kouuScales;
  }

  get kouuScale() {
    return this['$store'].getters.kouuScale === '自動' ? this.kouuAutoScale : this['$store'].getters.kouuScale;
  }

  get kouuAutoScale() {
    return this['$store'].getters.kouuAutoScale;
  }

  get ruikaScale() {
    return this['$store'].getters.ruikaScale === '自動' ? this['$store'].getters.ruikaAutoScale : this['$store'].getters.ruikaScale;
  }

  get ruikaAutoScale() {
    return this['$store'].getters.ruikaAutoScale;
  }

  get yskSpan() {
    return this['$store'].getters.yskSpan;
  }

  get targetTime() {
    if (this.propTargetTime) {
      return this.propTargetTime;
    }
    return this['$store'].getters.targetTime;
  }

  created() {
    window.addEventListener('resize', () => {
      this.updateResult();
    });
  }

  getOneFromSimResult(key) {
    return this.result.simResult.map((obj) => (Math.floor(obj[key]) <= -999 ? null : obj[key])).slice(0, 37 + 6 * this['$store'].getters.yskSpan);
  }

  get telemeterBasinStore() {
    return useTelemeterBasinStore();
  }

  getTelemeterBasinData() {
    const { observatories } = this.telemeterBasinStore;
    if (!observatories || !this.stationId) {
      return {
        bsnRain10mList: [],
        bsnRainIncList: [],
      };
    }

    const { stationsMap } = this['$store'].getters;
    const { code } = stationsMap[this.stationId];

    const stationData = observatories[code];
    if (!stationData) {
      return {
        bsnRain10mList: [],
        bsnRainIncList: [],
      };
    }

    const dataTimeArray = this.getOneFromSimResult('datetime');
    const list = dataTimeArray.slice(0, 37 + 6 * this['$store'].getters.yskSpan)
      .map((dateTime) => {
        const targetTime = parseInt(dateTime.replace(/[-:]/g, '').replace(' ', ''), 10);
        const matchingData = stationData.find((data) => data.obsTime === targetTime);
        return {
          bsnRain10m: matchingData ? matchingData.bsnRain10m : null,
          bsnRainInc: matchingData ? matchingData.bsnRainInc : null,
        };
      });

    return {
      bsnRain10mList: list.map((item) => item.bsnRain10m),
      bsnRainIncList: list.map((item) => item.bsnRainInc),
    };
  }

  @Watch('result')
  @Watch('yskSpan')
  @Watch('rerender')
  public async updateResult(): Promise<void> {
    if (![ContentsId.AutoCalculation, ContentsId.ManualCalculation, ContentsId.CustomCalculation].includes(this.tabId)) return;

    const RArray = this.getOneFromSimResult('r');
    const RuikaArray = this.getOneFromSimResult('ruika');
    const dataTimeArray = this.getOneFromSimResult('datetime');
    const { bsnRain10mList, bsnRainIncList } = this.getTelemeterBasinData();
    const RMaxNum = Math.max(...RArray, 5, ...bsnRain10mList);
    const RuikaMaxNum = Math.max(...RuikaArray, 5, ...bsnRainIncList);

    this['$store'].commit('setKouuAutoScale', getMaxScale(RMaxNum));
    this['$store'].commit('setRuikaAutoScale', getMaxScale(RuikaMaxNum));

    // telemeterBasinStoreのbsnRain10mデータセットを作成
    const telemeterBasinRain10mDataset = {
      type: 'line',
      label: '実績降雨強度',
      fill: false,
      data: bsnRain10mList,
      pointRadius: 0,
      borderWidth: 2,
      borderDash: [2, 2],
      borderColor: '#000060',
      style: 'border-top-color: #000060;',
      fixed: true,
      showUsageGuide: bsnRain10mList.some((val) => val !== null),
      yAxisID: 'y-axis-1',
    };

    const telemeterBasinRainIncDataset = {
      type: 'line',
      label: '実績累加雨量',
      fill: false,
      data: bsnRainIncList,
      pointRadius: 0,
      borderWidth: 2,
      borderDash: [2, 2],
      borderColor: '#006000',
      style: 'border-top-color: #006000;',
      fixed: true,
      showUsageGuide: bsnRainIncList.some((val) => val !== null),
      yAxisID: 'y-axis-2',
    };

    this.chartData = {
      labels: dataTimeArray.slice(0, 37 + 6 * this['$store'].getters.yskSpan),
      datasets: [
        {
          type: 'bar',
          label: '雨量',
          data: RArray,
          borderWidth: 1,
          borderColor: this.colors['降雨強度']?.color ?? '#8080ff',
          style: `border-top-color: ${this.colors['降雨強度']?.color ?? '#8080ff'};`,
          fixed: true,
          yAxisID: 'y-axis-1',
        },
        {
          type: 'line',
          label: '累加',
          fill: false,
          data: RuikaArray,
          pointRadius: 0,
          borderWidth: 2,
          borderColor: this.colors['累加雨量']?.color ?? '#00ff7f',
          style: `border-top-color: ${this.colors['累加雨量']?.color ?? '#00ff7f'};`,
          yAxisID: 'y-axis-2',
        },
        telemeterBasinRain10mDataset,
        telemeterBasinRainIncDataset,
      ],
    };

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.chartOptions = {
      responsive: true,
      // maintainAspectRatio: true!this.isMobile,
      maintainAspectRatio: false,
      tooltips: {
        callbacks: {
          label: (tooltipItem, obj) => `${obj.datasets[tooltipItem.datasetIndex].label}:${Number(tooltipItem.value).toFixed(1)}`,
        },
      },
      legend: {
        labels: {
          filter: () => false,
        },
      },
      annotation: {
        annotations: [{
          type: 'line',
          scaleID: 'x-axis-0',
          value: this.targetTime,
          borderWidth: 2,
          borderColor: 'gray',
        }],
      },
      scales: {
        xAxes: [
          {
            type: 'time',
            time: {
              unit: 'hour',
              displayFormats: {
                hour: 'HH:mm',
              },
              stepSize: this.gridPitch,
            },
            gridLines: {
              color: '#999',
            },
            ticks: {
              fontSize: 14,
              fontColor: 'black',
            },
          },
          {
            type: 'time',
            time: {
              unit: 'day',
              displayFormats: {
                day: 'MM/DD',
              },
            },
            ticks: {
              maxRotation: 0,
              minRotation: 0,
              fontSize: 14,
              fontColor: 'black',
            },
            gridLines: {
              color: '#999',
              drawTicks: false,
              drawBorder: false,
              drawOnChartArea: false,
              zeroLineColor: 'rgba(0, 0, 0, 0.1)',
            },
          },
        ],
        yAxes: [
          {
            afterFit(scaleInstance) {
              // eslint-disable-next-line no-param-reassign
              scaleInstance.width = 50;
            },
            id: 'y-axis-1',
            type: 'linear',
            position: 'left',
            display: true,
            gridLines: {
              color: '#999',
            },
            ticks: {
              // callback: (value) => {
              //   if (value % 1 !== 0) return null;
              //   const n = value % (this.kouuScale / 5);
              //   return n === 0 ? String(value) : null;
              // },
              stepSize: (this.kouuScale / 5),
              // maxTicksLimit: 6,
              // min: 0,
              max: this.kouuScale,
              fontSize: 14,
              fontColor: 'black',
            },
          },
          {
            afterFit(scaleInstance) {
            // eslint-disable-next-line no-param-reassign
              scaleInstance.width = 50;
            },
            id: 'y-axis-2',
            type: 'linear',
            position: 'right',
            display: true,
            afterBuildTicks: (scale) => {
              let num = this.ruikaScale;
              // eslint-disable-next-line no-param-reassign
              scale.ticks = [num];
              const step = this.ruikaScale / 5;
              while (scale.ticks.length <= 5) {
                num -= step;
                // eslint-disable-next-line no-param-reassign
                scale.ticks.push(num);
              }
            },
            gridLines: {
              color: '#999',
            },
            ticks: {
              // callback(value: any) { return ((value % 50) === 0) ? value : ''; },
              maxTicksLimit: 6,
              min: 0,
              max: this.ruikaScale,
              fontSize: 14,
              fontColor: 'black',
            },
          },
        ],
      },
    };
  }
}
